import { useState, useMemo } from "react";
import "./MobileCommunications.scss";
import {
  CheckCircleIcon,
  InfoIcon,
  AlertIcon,
  ArrowDownIcon,
  CalendarIcon,
  SparklesIcon,
  SignalIcon,
  ArrowLeftIcon,
  ChevronRightIcon,
} from "../../../../../shared/cockpit/components/icons/Icon";
import {
  type Plan,
  type Discount,
  discounts,
  omoSteps,
  mockPlans,
  mockBusinessSmartPlans,
  mockGigaMobilPlans,
} from "../Placeholders/MobilePlaceholder";

interface MobileCommunicationsProps {
  onNext: () => void;
}

type CategoryTab = "prime" | "smart" | "giga";

function MobileCommunications({ onNext }: MobileCommunicationsProps) {
  const [activePlan, setActivePlan] = useState<Plan | null>(null);
  const [contractType, setContractType] = useState<"new" | "vvl">("new");
  const [activeCategory, setActiveCategory] = useState<CategoryTab>("prime");
  const [selectedDiscount, setSelectedDiscount] = useState<Discount>(
    discounts[0],
  );
  const [selectedOmo, setSelectedOmo] = useState(omoSteps[0]);

  const plansToDisplay = useMemo(() => {
    switch (activeCategory) {
      case "smart":
        return mockBusinessSmartPlans;
      case "giga":
        return mockGigaMobilPlans;
      default:
        return mockPlans;
    }
  }, [activeCategory]);

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
    }).format(amount);

  const prices = useMemo(() => {
    if (!activePlan) return { avg: 0, m1_12: 0, m13_24: 0 };

    const { price } = activePlan;
    const { type, value, duration } = selectedDiscount;

    let m1_12 = price;
    let m13_24 = price;

    switch (type) {
      case "flat":
        m1_12 -= value;
        m13_24 -= value;
        break;

      case "percent":
        m1_12 = price * (1 - value / 100);
        if (duration > 12) {
          m13_24 = price * (1 - value / 100);
        }
        break;

      case "free":
        const freeInYear1 = Math.min(duration, 12);
        const freeInYear2 = Math.max(0, duration - 12);
        m1_12 = (price * (12 - freeInYear1)) / 12;
        m13_24 = (price * (12 - freeInYear2)) / 12;
        break;
    }

    return {
      m1_12,
      m13_24,
      avg: (m1_12 + m13_24) / 2,
    };
  }, [activePlan, selectedDiscount]);

  if (activePlan) {
    return (
      <div className="mobile-config">
        <button
          className="mobile-config__back-btn"
          onClick={() => {
            setActivePlan(null);
            setSelectedDiscount(discounts[0]);
          }}
        >
          <ArrowLeftIcon /> Zurück zur Tarifauswahl
        </button>

        <div className="mobile-config__card">
          <header className="mobile-config__header">
            <div className="mobile-config__title-group">
              <CheckCircleIcon className="mobile-config__check-circle" />
              <h3>{activePlan.name} ausgewählt</h3>
              <span className="mobile-config__badge">SIM only</span>
            </div>
            <div className="mobile-config__basis-price">
              € Basis: {formatPrice(activePlan.price)} €
            </div>
          </header>

          <section className="mobile-config__section">
            <label className="mobile-config__label">Rabatt wählen</label>
            <div className="mobile-config__discount-grid">
              {discounts.map((d) => (
                <button
                  key={d.id}
                  className={`mobile-config__discount-btn ${selectedDiscount.id === d.id ? "mobile-config__discount-btn--active" : ""}`}
                  onClick={() => setSelectedDiscount(d)}
                >
                  {d.label}
                </button>
              ))}
            </div>
            {selectedDiscount.id !== "none" && (
              <p className="mobile-config__discount-info">
                {selectedDiscount.type === "percent"
                  ? `${selectedDiscount.value}% Rabatt für ${selectedDiscount.duration} Monate`
                  : selectedDiscount.type === "free"
                    ? `${selectedDiscount.duration} Monate gratis`
                    : `${selectedDiscount.value}€ Rabatt monatlich`}
              </p>
            )}
          </section>

          <section className="mobile-config__section mobile-config__omo-section">
            <div className="mobile-config__omo-row">
              <div className="mobile-config__omo-col">
                <label className="mobile-config__label">% OMO-Rate</label>
                <div className="mobile-config__omo-input-wrapper">
                  <input
                    type="text"
                    placeholder="0"
                    className="mobile-config__omo-input"
                  />
                  <InfoIcon className="mobile-config__info-icon" />
                </div>
              </div>
              <div className="mobile-config__omo-col">
                <label className="mobile-config__label">
                  % OMO-Rabattstufe
                </label>
                <select
                  className="mobile-config__omo-select"
                  value={selectedOmo.label}
                  onChange={(e) => {
                    const found = omoSteps.find(
                      (s) => s.label === e.target.value,
                    );
                    if (found) setSelectedOmo(found);
                  }}
                >
                  {omoSteps.map((s) => (
                    <option key={s.label} value={s.label}>
                      {s.label} — {formatPrice(Math.abs(s.marginEffect))}€
                    </option>
                  ))}
                </select>
                <div className="mobile-config__omo-warning">
                  <AlertIcon /> Keine OMO-Matrix im Tarif. Werte werden
                  prozentual berechnet.
                </div>
              </div>
            </div>
          </section>

          <div className="mobile-config__options-dropdown">
            <span>
              <ArrowDownIcon /> Preisübersicht nach Zeitraum
            </span>
          </div>

          <div className="mobile-config__overview">
            <div className="mobile-config__overview-header">
              <CalendarIcon /> <span>Preisübersicht</span>
            </div>
            <div className="mobile-config__price-cards">
              <div className="mobile-config__price-card mobile-config__price-card--active">
                <span className="mobile-config__card-period">Monat 1–12</span>
                <span className="mobile-config__card-value">
                  {formatPrice(prices.m1_12)} € <small>€/mtl.</small>
                </span>
                {prices.m1_12 < activePlan.price && (
                  <span className="mobile-config__card-badge">
                    <SparklesIcon /> Aktionspreis
                  </span>
                )}
              </div>
              <div className="mobile-config__price-card">
                <span className="mobile-config__card-period">Monat 13–24</span>
                <span className="mobile-config__card-value">
                  {formatPrice(prices.m13_24)} € <small>€/mtl.</small>
                </span>
                <span className="mobile-config__card-badge mobile-config__card-badge--normal">
                  Normalpreis
                </span>
              </div>
            </div>

            <div className="mobile-config__avg-row">
              <div className="mobile-config__avg-left">
                <span className="mobile-config__avg-icon">
                  <SparklesIcon />
                </span>
                <div>
                  <strong>Ø Durchschnitt pro Monat</strong>
                  <p>Berechnet über 24 Monate Laufzeit ⓘ</p>
                </div>
              </div>
              <div className="mobile-config__avg-right">
                <span className="mobile-config__avg-value">
                  {formatPrice(prices.avg)} € <small>€</small>
                </span>
              </div>
            </div>
          </div>

          <footer className="mobile-config__footer">
            <div className="mobile-config__total-info">
              <div className="mobile-config__total-item">
                <span className="mobile-config__total-label">
                  Ø MONATSPREIS
                </span>
                <span className="mobile-config__total-value">
                  {formatPrice(prices.avg)} € <small>€</small>
                </span>
              </div>
              <div className="mobile-config__total-item mobile-config__total-item--margin">
                <span className="mobile-config__total-label">MARGE</span>
                <span className="mobile-config__total-value mobile-config__total-value--negative">
                  {formatPrice(selectedOmo.marginEffect)} €
                </span>
              </div>
            </div>
            <button className="mobile-config__add-btn">
              + Zum Angebot hinzufügen <SparklesIcon />
            </button>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-plans">
      <header className="mobile-plans__header">
        <h2>Tarifkonfiguration</h2>
        <div className="mobile-plans__top-bar">
          <div className="mobile-plans__tabs">
            <button
              className={`mobile-plans__tab ${activeCategory === "prime" ? "mobile-plans__tab--active" : ""}`}
              onClick={() => setActiveCategory("prime")}
            >
              Business Prime
            </button>
            <button
              className={`mobile-plans__tab ${activeCategory === "smart" ? "mobile-plans__tab--active" : ""}`}
              onClick={() => setActiveCategory("smart")}
            >
              Business Smart
            </button>
            <button
              className={`mobile-plans__tab ${activeCategory === "giga" ? "mobile-plans__tab--active" : ""}`}
              onClick={() => setActiveCategory("giga")}
            >
              GigaMobil
            </button>
          </div>
          <div className="mobile-plans__controls">
            <div className="mobile-plans__vorlauf">
              <label>Vorlauf</label>
              <select>
                <option>Kein</option>
              </select>
            </div>
            <div className="mobile-plans__counter">
              <button>-</button>
              <input type="text" value="1" readOnly />
              <button>+</button>
              <span>SIM</span>
            </div>
            <div className="mobile-plans__contract-toggle">
              <button
                className={contractType === "new" ? "active" : ""}
                onClick={() => setContractType("new")}
              >
                Neuvertrag
              </button>
              <button
                className={contractType === "vvl" ? "active" : ""}
                onClick={() => setContractType("vvl")}
              >
                VVL
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="mobile-plans__sub-tabs">
        <button className="active">SIM</button>
        <button>Basic</button>
        <button>Smart</button>
        <button>Premium</button>
        <button>Spec.</button>
        <select className="mobile-plans__promo-select">
          <option>Keine Aktion</option>
        </select>
      </div>

      <div className="mobile-plans__table-container">
        <table className="mobile-plans__table">
          <thead>
            <tr>
              <th>TARIF</th>
              <th>DATEN</th>
              <th>PREIS</th>
              <th>AKTION</th>
            </tr>
          </thead>
          <tbody>
            {plansToDisplay.map((plan) => (
              <tr key={plan.id}>
                <td>
                  <div className="mobile-plans__plan-name">
                    <SignalIcon className="mobile-plans__signal-icon" />
                    {plan.name}
                    {plan.isTop && (
                      <span className="mobile-plans__top-badge">TOP</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className="mobile-plans__data-badge">{plan.data}</span>
                </td>
                <td>
                  <strong>{formatPrice(plan.price)} €</strong>
                  <small>/mtl.</small>
                </td>
                <td>
                  <button
                    className="mobile-plans__select-btn"
                    onClick={() => setActivePlan(plan)}
                  >
                    Wählen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mobile-plans__footer">
        <button className="mobile-plans__next-btn" onClick={onNext}>
          Weiter zu Festnetz <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
}

export default MobileCommunications;
